import { AnimatePresence, motion } from 'motion/react';

import { Icons } from '@/assets/icons/icons';
import { Tooltip, type TooltipProps } from '@/components/ui/Tooltip';
import type { KeyName } from '@/lib/key';
import { classList } from '@/utils/cn';

interface SendButtonProps {
  isBusy: boolean;
  onSend: () => void;
  onStop: () => void;
  disabled?: boolean;
  tooltipSide?: TooltipProps['side'];
  tooltipHotkey?: readonly KeyName[];
  voiceMode?: { onStart: () => void; disabled: boolean };
  className?: string;
}

export function SendButton({
  isBusy,
  onSend,
  onStop,
  disabled = false,
  tooltipSide,
  tooltipHotkey,
  voiceMode,
  className,
}: SendButtonProps) {
  const showsVoiceMode = !isBusy && Boolean(voiceMode);

  const handleClick = () => {
    if (isBusy) {
      onStop();
    } else if (voiceMode) {
      voiceMode.onStart();
    } else {
      onSend();
    }
  };

  const tooltipLabel = isBusy
    ? 'Stop'
    : showsVoiceMode
      ? 'Start voice mode'
      : 'Send';
  const resolvedTooltipHotkey =
    isBusy || showsVoiceMode
      ? undefined
      : (tooltipHotkey ?? (['enter'] as const));

  const isVisuallyDisabled =
    !isBusy && (voiceMode ? voiceMode.disabled : disabled);

  return (
    <Tooltip
      label={tooltipLabel}
      hotkey={resolvedTooltipHotkey}
      side={tooltipSide}
    >
      <motion.button
        className={classList(
          'flex items-center justify-center',
          'rounded-full hover:rounded-full',
          'size-8',
          'bg-gray-12 text-gray-1',
          'cursor-default disabled:cursor-not-allowed',
          className
        )}
        onClick={handleClick}
        animate={{
          opacity: isVisuallyDisabled ? 0.75 : 1,
        }}
        whileTap={
          !isVisuallyDisabled
            ? {
                scale: 0.9,
                transition: {
                  type: 'tween',
                  duration: 0.1,
                },
              }
            : undefined
        }
        disabled={isVisuallyDisabled}
        aria-label={
          isBusy
            ? 'Stop generating'
            : showsVoiceMode
              ? 'Start voice mode'
              : 'Send message'
        }
        aria-busy={isBusy}
        aria-disabled={isVisuallyDisabled}
        data-testid={showsVoiceMode ? 'voice-mode-button' : undefined}
        type="button"
      >
        <AnimatePresence initial={false} mode="wait">
          {isBusy ? (
            <motion.span
              key="icon-stop-chat"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', damping: 8 }}
            >
              <Icons.stop className="transform-gpu size-3.5 translate-x-[0.5px]" />
            </motion.span>
          ) : showsVoiceMode ? (
            <motion.span
              key="icon-voice-mode"
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.6, transition: { duration: 0.08 } }}
              transition={{ type: 'spring', stiffness: 600, damping: 30 }}
            >
              <Icons.dictation
                className="transform-gpu size-4"
                strokeWidth={2}
              />
            </motion.span>
          ) : (
            <motion.span
              key="icon-send-text"
              initial={{ opacity: 0, y: 3 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4, transition: { type: 'keyframes' } }}
              transition={{ type: 'spring', damping: 8 }}
            >
              <Icons.send className="transform-gpu size-4" />
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>
    </Tooltip>
  );
}
